import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import IconGoBack from '../../../components/icons/IconGoBack';
import IconCursorTool from '../../../components/icons/IconCursorTool';
import IconPencilTool from '../../../components/icons/IconPencilTool';
import IconShapeRect from '../../../components/icons/IconShapeRect';
import IconShapeEllipse from '../../../components/icons/IconShapeEllipse';
import IconShapeArrow from '../../../components/icons/IconShapeArrow';
import IconTextTool from '../../../components/icons/IconTextTool';
import IconImageTool from '../../../components/icons/IconImageTool';
import IconVideoTool from '../../../components/icons/IconVideoTool';
import IconTrashTool from '../../../components/icons/IconTrashTool';

const COLOR_SWATCHES = ['#f3f4f6', '#e0533a', '#e0a83a', '#30e846', '#4f8ff0', '#8a5ce0', '#000000'];

const TOOLS = [
   { tool: 'select', Icon: IconCursorTool, titleKey: 'canvasToolSelect' },
   { tool: 'PENCIL', Icon: IconPencilTool, titleKey: 'canvasToolPencil' },
   { tool: 'RECT', Icon: IconShapeRect, titleKey: 'canvasToolRect' },
   { tool: 'ELLIPSE', Icon: IconShapeEllipse, titleKey: 'canvasToolEllipse' },
   { tool: 'ARROW', Icon: IconShapeArrow, titleKey: 'canvasToolArrow' },
   { tool: 'TEXT', Icon: IconTextTool, titleKey: 'canvasToolText' },
   { tool: 'IMAGE', Icon: IconImageTool, titleKey: 'canvasToolImage' },
   { tool: 'VIDEO_EMBED', Icon: IconVideoTool, titleKey: 'canvasToolVideoEmbed' },
];
// Tools whose color swatch actually affects the drawn element - IMAGE has no
// stroke/fill of its own, select is not a drawing tool at all.
const COLOR_AWARE_TOOLS = new Set(['PENCIL', 'RECT', 'ELLIPSE', 'ARROW', 'TEXT']);

export default function CanvasToolbar({ tool, setTool, color, setColor, onPickImage, onAddVideoEmbed, viewport, backTo, lang, hasSelection, onDeleteSelected, presentList }) {
   const navigate = useNavigate();
   const fileInputRef = useRef(null);
   const present = presentList || [];
   const isOnline = present.length > 0;
   // Comma-joined names in the pill itself when there's room for it (1-3
   // people - a group canvas can genuinely have several at once now, not
   // just the one student the old singular "Студент присутній" framing
   // assumed), falling back to a count once it'd otherwise overflow the
   // toolbar - full list always available on hover either way.
   const namesLabel = present.length === 0 ? ''
      : present.length <= 3 ? present.join(', ')
      : lang.canvasPresenceCount.replace('{count}', present.length);

   const handleImageToolClick = () => {
      setTool('IMAGE');
      fileInputRef.current && fileInputRef.current.click();
   };
   const handleFileChange = (e) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (file) onPickImage(file);
   };

   return (
      <div className='topic-canvas-toolbar' onMouseDown={(e) => e.stopPropagation()}>
         <span className='topic-canvas-back-btn' onClick={() => navigate(backTo)} title={lang.canvasBackToTopic}>
            <IconGoBack />
         </span>
         <span className='topic-canvas-toolbar-sep' />
         <span className={'workspace-presence-indicator' + (isOnline ? ' online' : '')}
            title={isOnline ? present.join(', ') : lang.canvasPresenceNoOne}>
            <i className='workspace-presence-dot' />
            {isOnline ? namesLabel : lang.canvasPresenceNoOne}
         </span>
         <span className='topic-canvas-toolbar-sep' />
         {TOOLS.map(({ tool: t, Icon, titleKey }) => (
            <span key={t} className={'topic-canvas-tool-btn' + (tool === t ? ' active' : '')}
               onClick={() => (t === 'IMAGE' ? handleImageToolClick() : t === 'VIDEO_EMBED' ? onAddVideoEmbed() : setTool(t))}
               title={lang[titleKey]}>
               <Icon />
            </span>
         ))}
         <span className='topic-canvas-toolbar-sep' />
         <span className={'topic-canvas-tool-btn topic-canvas-delete-btn' + (hasSelection ? '' : ' disabled')}
            onClick={() => hasSelection && onDeleteSelected()} title={lang.canvasDeleteElement}>
            <IconTrashTool />
         </span>
         {COLOR_AWARE_TOOLS.has(tool) ? (
            <>
               <span className='topic-canvas-toolbar-sep' />
               <span className='topic-canvas-color-swatches'>
                  {COLOR_SWATCHES.map((c) => (
                     <span key={c} className={'topic-canvas-color-swatch' + (color === c ? ' active' : '')}
                        style={{ background: c }} onClick={() => setColor(c)} />
                  ))}
                  <input type='color' className='topic-canvas-color-input' value={color} onChange={(e) => setColor(e.target.value)} />
               </span>
            </>
         ) : ''}
         <input ref={fileInputRef} type='file' accept='image/*' style={{ display: 'none' }} onChange={handleFileChange} />
         <span className='topic-canvas-toolbar-sep' />
         <span className='topic-canvas-zoom-controls'>
            <span className='topic-canvas-tool-btn' onClick={() => viewport.zoomBy(1.2)}>+</span>
            <span className='topic-canvas-zoom-level'>{Math.round(viewport.zoom * 100)}%</span>
            <span className='topic-canvas-tool-btn' onClick={() => viewport.zoomBy(1 / 1.2)}>−</span>
            <span className='topic-canvas-tool-btn topic-canvas-zoom-reset' onClick={viewport.resetView}>{lang.canvasResetView}</span>
         </span>
      </div>
   );
}
